import useInitializeApp from "./useInitializeApp";
import {
  SHOULD_PRINT,
  usePrintOnMount,
} from "components/Nav/Drawers/RightDrawer/PrintOrDownloadButton";
import useSWHeartBeat from "./useSWHeartBeat";
import { useEffect, useRef } from "react";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { SLOW_AUTO_REFRESH_INTERVAL } from "constants/intervals";
import { useMount, usePrevious } from "react-use";
import LogRocket from "logrocket";
import {
  ONE_DAY_OF_MS,
  daylightTimeZones,
  determineCurrentTimeZone,
  getIsDaylightSavings,
  standardTimeZones,
} from "utils/timeUtils";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
import {
  useEffectOnce,
  useGetPageFromPath,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { timeZonesWikiData } from "utils/timeZonesWikiData";
import { useAtom } from "jotai";
import { isTimezoneManuallyOverriddenAtom } from "components/Settings/TimeZoneController";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useFlightConfig,
  useIsFlightConfigInitialized,
} from "components/DataGetter/FlightConfig/useFlightConfig";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { useDispatch } from "react-redux";
import {
  defaultTimeAxisContext,
  setTimeAxisState,
  setShouldFetchNewShifts,
} from "providers/GlobalStateProvider";
import { useSyncCypressOverrides } from "utils/customHooks/syncCypressOverrides";
import { useClearProcessTilesFilterStateWhenCkeyChanges } from "pages/DashboardPages/ProcessTilesControls/useProcessTilesFilterState";

/** isolating these hooks in their own component prevents excess re-renders in the parent component. */
export function AppHooks() {
  return (
    <>
      <InitializeApp />
      <AutoRefreshTimer />
      <RestartLogRocketSessionOnInterval />
      {SHOULD_PRINT ? <PrintOnMount /> : null}
      <SWHeartBeat />
      <SetTimeZoneFromWorkscheduleConfig />
      <LDClearLocalStorageItem />
      <LDSetLocalStorageItem />
      <ClearWorkSchedulesOnCkeyChange />
      <ResetTimelineTransformOnPageChange />
      <SyncCypressOverrides />
      <ClearProcessTilesFilterStateWhenCkeyChanges />
      <RefetchShiftsOnWorkScheduleChange />
      <SetProcessTileWidthMultiplier />
    </>
  );
}

const InitializeApp = () => {
  useInitializeApp();
  return null;
};

const AutoRefreshTimer = () => {
  useAutoRefreshTimer();
  return null;
};

const RestartLogRocketSessionOnInterval = () => {
  useRestartLogRocketSessionOnInterval();
  return null;
};

const PrintOnMount = () => {
  usePrintOnMount();
  return null;
};

const SWHeartBeat = () => {
  useSWHeartBeat();
  return null;
};

const SetTimeZoneFromWorkscheduleConfig = () => {
  useSetTimeZoneFromWorkscheduleConfig();
  return null;
};

const LDClearLocalStorageItem = () => {
  useLDClearLocalStorageItem();
  return null;
};

const LDSetLocalStorageItem = () => {
  useLDSetLocalStorageItem();
  return null;
};

const ClearWorkSchedulesOnCkeyChange = () => {
  useClearWorkSchedulesOnCkeyChange();
  return null;
};

const ResetTimelineTransformOnPageChange = () => {
  useResetTimelineTransformOnPageChange();
  return null;
};

const SyncCypressOverrides = () => {
  useSyncCypressOverrides();
  return null;
};

const ClearProcessTilesFilterStateWhenCkeyChanges = () => {
  useClearProcessTilesFilterStateWhenCkeyChanges();
  return null;
};

const RefetchShiftsOnWorkScheduleChange = () => {
  useRefetchShiftsOnWorkScheduleChange();
  return null;
};

/**
 * This hook monitors for changes in the workScheduleId and triggers a shift refetch
 * when it changes to ensure we always have the correct shifts for the current schedule.
 */
function useRefetchShiftsOnWorkScheduleChange() {
  const dispatch = useDispatch();
  const currentWorkScheduleId = useWorkScheduleIdOnCurrentPage();
  const prevWorkScheduleId = usePrevious(currentWorkScheduleId);
  const workScheduleIdChanged =
    currentWorkScheduleId &&
    prevWorkScheduleId &&
    currentWorkScheduleId !== prevWorkScheduleId;

  useEffect(() => {
    if (workScheduleIdChanged) {
      console.log(
        `WorkScheduleId changed from ${prevWorkScheduleId} to ${currentWorkScheduleId}. Refetching shifts...`
      );
      // Set the flag to trigger a shift refetch
      dispatch(setShouldFetchNewShifts(true));
    }
  }, [
    currentWorkScheduleId,
    prevWorkScheduleId,
    workScheduleIdChanged,
    dispatch,
  ]);
}

/** if auto-refresh setting is enabled, refreshes on the inteval specified in Settings page,
 * otherwise, refreshes every 24hrs
 */
function useAutoRefreshTimer() {
  const {
    isAutoRefreshEnabled,
    setIsAutoRefreshEnabled,
    autoRefreshTimeInterval,
  } = useSettings();

  const refreshTimerRef = useRef(null as number | null);
  useEffect(() => {
    refreshTimerRef.current = window.setTimeout(
      () => {
        window.location.reload();
      },
      isAutoRefreshEnabled
        ? autoRefreshTimeInterval
        : SLOW_AUTO_REFRESH_INTERVAL
    );
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isAutoRefreshEnabled, autoRefreshTimeInterval, setIsAutoRefreshEnabled]);
}

/** to avoid over-long LogRocket sessions, which take too long to load on LogRocket,
 * restart the LogRocket session once a day to limit session length to 24hrs
 */
function useRestartLogRocketSessionOnInterval() {
  useMount(() => {
    window.setInterval(() => {
      LogRocket.startNewSession();
    }, ONE_DAY_OF_MS);
  });
}

function useClearWorkSchedulesOnCkeyChange() {
  const ckey = useCustomerKey();
  const [, setWorkScheduleConfigJsonList] = useWorkScheduleConfigJsonListAtom();
  const prevCkey = usePrevious(ckey);
  useEffect(() => {
    if (prevCkey && prevCkey !== ckey) {
      setWorkScheduleConfigJsonList([]);
    }
  }, [prevCkey, ckey, setWorkScheduleConfigJsonList]);
}

function useSetTimeZoneFromWorkscheduleConfig() {
  const { isCustomerTilesPage, isSchedulePage } = useGetPageFromPath();
  const [workScheduleConfigs] = useWorkScheduleConfigJsonListAtom();
  const workScheduleConfigId = useWorkScheduleIdOnCurrentPage();
  const workScheduleConfig = workScheduleConfigs.find(
    x => x.id === workScheduleConfigId
  );
  const timeZone = workScheduleConfig?.timeZone;

  const {
    timeZoneLocaleString,
    setTimeZoneLocaleString,
    shouldUseCustomTimeZone,
    setShouldUseCustomTimeZone,
    shouldAutoSwitchToCustomerTimezone,
  } = useSettings();
  const [isTimezoneManuallyOverridden, setIsTimezoneManuallyOverridden] =
    useAtom(isTimezoneManuallyOverriddenAtom);

  const ckey = useCustomerKey();
  const prevCkey = usePrevious(ckey);
  const prevWorkScheduleConfigId = usePrevious(workScheduleConfigId);
  const didCkeyChange = ckey && prevCkey && ckey !== prevCkey;
  const didWorkScheduleChange =
    workScheduleConfigId &&
    prevWorkScheduleConfigId &&
    workScheduleConfigId !== prevWorkScheduleConfigId;
  // reset isTimezoneManuallyOverridden when workSchedule or customer changes
  useEffect(() => {
    if (
      isTimezoneManuallyOverridden &&
      shouldAutoSwitchToCustomerTimezone &&
      (didCkeyChange || didWorkScheduleChange)
    ) {
      setIsTimezoneManuallyOverridden(false);
    }
  }, [
    ckey,
    isTimezoneManuallyOverridden,
    didCkeyChange,
    didWorkScheduleChange,
    setIsTimezoneManuallyOverridden,
    shouldAutoSwitchToCustomerTimezone,
  ]);

  const [
    {
      general: { autoSetTimezoneOverride },
    },
  ] = useFlightConfig();
  // automatically set timeZone override to the timezone found in the workScheduleConfig (unless user has manually selected timezone from a TimeZoneController dropdown)
  useEffect(() => {
    if (
      !autoSetTimezoneOverride ||
      isTimezoneManuallyOverridden ||
      isCustomerTilesPage ||
      isSchedulePage
    ) {
      return;
    }
    if (timeZone && timeZone !== timeZoneLocaleString) {
      const currentTimezone = determineCurrentTimeZone();
      let timeZoneData = timeZonesWikiData.find(
        x => x["TZ database name"] === timeZone
      );

      // workschedule configs sometimes use "deprecated" timezone database names -> find the associated "canonical" database name
      const isDeprecated = timeZoneData && timeZoneData.Status === "Deprecated";
      const isDST = getIsDaylightSavings(new Date());
      const offsetKey = isDST ? "UTC DST offset ±hh:mm" : "UTC offset ±hh:mm";
      if (isDeprecated) {
        timeZoneData = timeZonesWikiData.find(
          x =>
            timeZoneData &&
            x[offsetKey] === timeZoneData[offsetKey] &&
            x.Status !== "Deprecated"
        );
      }
      const canonicalTimeZone = timeZoneData?.["TZ database name"];

      if (!canonicalTimeZone) {
        console.log(
          "no canonical timezone found with workScheduleConfig TZ name:",
          timeZone
        );
        return;
      }

      // if the timezone found in the workScheduleConfig is different from the current timezone, set the timezone to the workScheduleConfig timezone
      const nextShouldUseCustomTimeZone =
        canonicalTimeZone !== currentTimezone.localeString;
      if (nextShouldUseCustomTimeZone !== shouldUseCustomTimeZone) {
        setShouldUseCustomTimeZone(nextShouldUseCustomTimeZone);
      }
      if (!nextShouldUseCustomTimeZone) {
        return;
      }

      // workScheduleConfig timeZone matches to timeUtils > (daylightTimeZones/standardTimeZones), but we need the timeZoneLocaleString from timeZonesWikiData
      // -> 1. find associated timeUtils timezone containing e.g. "UTC offset ±hh:mm": "-07:00"
      const offsetInHHMM = timeZoneData?.[offsetKey];
      // -> 2. convert "-07:00" into minutes
      const offsetInMinutes =
        offsetInHHMM &&
        offsetInHHMM
          .split(":")
          .reduce((acc, val, i) => acc + Number(val) * (i === 0 ? 60 : 1), 0);
      const timeZonesData = isDST ? daylightTimeZones : standardTimeZones;
      // -> 3. get associated timeZoneLocaleString based on the offset
      const associatedTimezone = timeZonesData.find(
        x => x.offsetInMin === offsetInMinutes
      );
      const associatedTimezoneLocaleString = associatedTimezone?.localeString;

      if (
        shouldAutoSwitchToCustomerTimezone &&
        associatedTimezoneLocaleString &&
        associatedTimezoneLocaleString !== timeZoneLocaleString
      ) {
        console.log(
          "Setting timezone to:",
          associatedTimezoneLocaleString,
          "( from workScheduleConfig TZ name:",
          timeZone,
          ")"
        );
        setTimeZoneLocaleString(associatedTimezoneLocaleString);
      }
    }
  }, [
    timeZone,
    setShouldUseCustomTimeZone,
    shouldUseCustomTimeZone,
    setTimeZoneLocaleString,
    timeZoneLocaleString,
    isTimezoneManuallyOverridden,
    isCustomerTilesPage,
    isSchedulePage,
    autoSetTimezoneOverride,
    shouldAutoSwitchToCustomerTimezone,
  ]);
}

/** flightClearLocalStorageItem: `Set to a string to automatically clear one item from localStorage e.g. "timeZoneLocaleString" -> clear timezone setting; most Settings are in SettingsManager.tsx` */
function useLDClearLocalStorageItem() {
  const { flightClearLocalStorageItem } = useFlagsWithDefaults();
  const { setShouldUseCustomTimeZone } = useSettings();
  useEffect(() => {
    if (
      flightClearLocalStorageItem &&
      window.localStorage.getItem(flightClearLocalStorageItem)
    ) {
      console.log(
        `clearing localStorage item ${flightClearLocalStorageItem} and reloading:`,
        flightClearLocalStorageItem
      );
      window.localStorage.removeItem(flightClearLocalStorageItem);
      // if we removed timeZoneLocaleString, need to also setShouldUseCustomTimeZone to false
      if (flightClearLocalStorageItem === "timeZoneLocaleString") {
        setShouldUseCustomTimeZone(false);
      }
      window.location.reload();
    }
  }, [flightClearLocalStorageItem, setShouldUseCustomTimeZone]);
}

/** flightSetLocalStorageItem: `Set to a string with the format '<key>:<value>' where <value> is in JSON.stringified format e.g. 'timeZoneLocaleString:"Etc/GMT+6"' or e.g. 'dayStartMsOffset:0' to automatically set one item in localStorage; most Settings are in SettingsManager.tsx` */
function useLDSetLocalStorageItem() {
  const { flightSetLocalStorageItem } = useFlagsWithDefaults();
  const lsKey = flightSetLocalStorageItem
    ? flightSetLocalStorageItem.split(":")[0]
    : null;
  let lsValue: any = null;
  try {
    lsValue = flightSetLocalStorageItem
      ? JSON.parse(flightSetLocalStorageItem.split(":")?.[1])
      : null;
  } catch (error) {
    console.error(
      `Error parsing JSON from flightSetLocalStorageItem with value ${flightSetLocalStorageItem} :`,
      error
    );
  }
  const { setShouldUseCustomTimeZone } = useSettings();
  useEffect(() => {
    if (!lsKey || !lsValue) {
      return;
    }
    const matchingLSItem = window.localStorage.getItem(lsKey);
    if (!matchingLSItem || matchingLSItem !== lsValue) {
      console.log(
        `Setting localStorage item ${lsKey} to ${lsValue} and reloading:`
      );
      window.localStorage.setItem(lsKey, lsValue);
      // if we set timeZoneLocaleString, need to also setShouldUseCustomTimeZone to true
      if (lsKey === "timeZoneLocaleString") {
        setShouldUseCustomTimeZone(true);
      }
      window.location.reload();
    }
  }, [lsKey, lsValue, setShouldUseCustomTimeZone]);
}

/** when we change pages, reset the timeline transform to default */
function useResetTimelineTransformOnPageChange() {
  const { pageName } = useGetPageFromPath();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setTimeAxisState(defaultTimeAxisContext));
  }, [pageName, dispatch]);
}

/** setup some settings from flight config */
function SetProcessTileWidthMultiplier() {
  const { setTileWidthMultiplier } = useSettings();
  const [
    {
      general: { tileWidthMultiplier: tileWidthConfig },
    },
  ] = useFlightConfig();
  const [isFlightConfigInitialized] = useIsFlightConfigInitialized();
  useEffectOnce({
    callback: () => {
      setTileWidthMultiplier(tileWidthConfig);
    },
    shouldRun: isFlightConfigInitialized === "initialized",
    dependencies: [isFlightConfigInitialized],
  });
  return null;
}
